import React from 'react';

interface WelcomeModalProps {
  onGetStarted: () => void;
  onClose: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ onGetStarted, onClose }) => {
  const handleClose = onClose;
  const handleGetStarted = onGetStarted;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-lg border border-gray-200 relative">

        {/* Close */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Hero */}
        <div className="px-8 pt-8 pb-6">
          <p className="text-xs font-semibold text-orange-500 uppercase tracking-widest mb-2">It's this simple</p>
          <h2 className="text-gray-900 text-2xl font-bold leading-snug">
            Your book, print-ready in minutes.
          </h2>
          <p className="text-gray-500 text-sm mt-1">No design skills. No formatting headaches. Just your words.</p>
        </div>

        {/* 3 Horizontal Steps */}
        <div className="px-8 pb-6">
          <div className="grid grid-cols-3 gap-0">

            {/* Step 1 */}
            <div className="flex flex-col pr-4">
              <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <p className="text-gray-900 font-semibold text-sm mb-1">Paste your content</p>
              <p className="text-gray-500 text-xs leading-relaxed">
                Just your chapter titles and text. No special formatting needed — exactly how you'd write in a notes app.
              </p>
            </div>

            {/* Arrow 1 */}
            <div className="relative flex flex-col px-4 border-l border-r border-gray-100">
              <span className="absolute -left-2.5 top-4 text-gray-300 text-lg">›</span>
              <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <p className="text-gray-900 font-semibold text-sm mb-1">We format everything</p>
              <div className="space-y-1">
                {['Page numbers', 'Headers & footers', 'Table of contents', 'Print-ready layout'].map((item) => (
                  <p key={item} className="text-xs text-gray-500 flex items-center gap-1">
                    <span className="text-green-500 font-bold text-xs">✓</span> {item}
                  </p>
                ))}
              </div>
            </div>

            {/* Arrow 2 + Step 3 */}
            <div className="relative flex flex-col pl-4">
              <span className="absolute -left-2.5 top-4 text-gray-300 text-lg">›</span>
              <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <p className="text-gray-900 font-semibold text-sm mb-1">Cover, draft & submit</p>
              <p className="text-xs text-gray-500 leading-relaxed">
                Upload your own cover or get our{' '}
                <span className="text-orange-500 font-medium">cover image package</span>.
                Download your draft to preview, then submit — done.
              </p>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-100 flex items-center justify-between">
          <p className="text-xs text-gray-400">Takes just a few minutes.</p>
          <button
            onClick={handleGetStarted}
            className="bg-gray-900 text-white px-6 py-2.5 rounded-lg hover:bg-gray-800 font-medium transition-colors text-sm"
          >
            Let's go →
          </button>
        </div>

      </div>
    </div>
  );
};

export default WelcomeModal;
